import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material'
import React, { useEffect, useState } from 'react'

import { useAnalytics } from '../../contexts/AnalyticsContext'
import { AdminService } from '../../services/AdminService'
import { AnalyticsEvents } from '../../types/AnalyticsEvents'
import Database from '../../types/Database'

/**
 * StatisticsDashboard Component
 *
 * Displays system-wide statistics and recent administrative actions.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 7.5, 9.3
 */

const StatisticsDashboard: React.FC = () => {
  const { trackEvent } = useAnalytics()
  const [statistics, setStatistics] =
    useState<Database.SystemStatistics | null>(null)
  const [auditLogs, setAuditLogs] = useState<Database.AuditLogEntry[]>([])
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [isLoadingLogs, setIsLoadingLogs] = useState(true)
  const [statsError, setStatsError] = useState<string | null>(null)
  const [logsError, setLogsError] = useState<string | null>(null)

  useEffect(() => {
    // Track statistics dashboard view on mount
    try {
      trackEvent(AnalyticsEvents.ADMIN_STATISTICS_VIEWED, {
        action_type: 'view_statistics_dashboard'
      })
    } catch (error) {
      console.error('Failed to track admin statistics viewed event:', error)
    }

    loadStatistics()
    loadAuditLogs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadStatistics = async () => {
    setIsLoadingStats(true)
    setStatsError(null)
    try {
      const stats = await AdminService.getSystemStatistics()
      setStatistics(stats)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load statistics'
      setStatsError(errorMessage)
    } finally {
      setIsLoadingStats(false)
    }
  }

  const loadAuditLogs = async () => {
    setIsLoadingLogs(true)
    setLogsError(null)
    try {
      const logs = await AdminService.getRecentAuditLogs(10)
      setAuditLogs(logs)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load audit logs'
      setLogsError(errorMessage)
    } finally {
      setIsLoadingLogs(false)
    }
  }

  const formatActionText = (log: Database.AuditLogEntry): string => {
    switch (log.action) {
      case 'user_deleted':
        return `Deleted user ${log.targetEmail || log.targetId}`
      case 'user_promoted':
        return `Promoted ${log.targetEmail || log.targetId} to admin`
      case 'user_demoted':
        return `Demoted ${log.targetEmail || log.targetId} from admin`
      case 'office_capacity_changed':
        return `Changed office capacity from ${log.details.previousValue} to ${log.details.newValue}`
      case 'office_tier_changed':
        return `Changed office tier from ${log.details.previousValue} to ${log.details.newValue}`
      default:
        return `${log.action} on ${log.targetId}`
    }
  }

  const formatTimestamp = (timestamp: Date): string => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
    return date.toLocaleString()
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h4' gutterBottom>
        System Statistics
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Users */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              {isLoadingStats ? (
                <Box
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  minHeight={100}
                >
                  <CircularProgress size={40} />
                </Box>
              ) : statsError ? (
                <Alert severity='error' onClose={() => loadStatistics()}>
                  {statsError}
                </Alert>
              ) : (
                <>
                  <Typography color='textSecondary' gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant='h3'>
                    {statistics?.totalUsers || 0}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Total Offices */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              {isLoadingStats ? (
                <Box
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  minHeight={100}
                >
                  <CircularProgress size={40} />
                </Box>
              ) : statsError ? (
                <Alert severity='error' onClose={() => loadStatistics()}>
                  {statsError}
                </Alert>
              ) : (
                <>
                  <Typography color='textSecondary' gutterBottom>
                    Total Offices
                  </Typography>
                  <Typography variant='h3'>
                    {statistics?.totalOffices || 0}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Total Desks */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              {isLoadingStats ? (
                <Box
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                  minHeight={100}
                >
                  <CircularProgress size={40} />
                </Box>
              ) : statsError ? (
                <Alert severity='error' onClose={() => loadStatistics()}>
                  {statsError}
                </Alert>
              ) : (
                <>
                  <Typography color='textSecondary' gutterBottom>
                    Total Desks
                  </Typography>
                  <Typography variant='h3'>
                    {statistics?.totalDesks || 0}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Offices by Tier */}
      <Typography variant='h5' gutterBottom sx={{ mt: 4 }}>
        Offices by Subscription Tier
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {isLoadingStats ? (
          <Grid item xs={12}>
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              minHeight={100}
            >
              <CircularProgress size={40} />
            </Box>
          </Grid>
        ) : statsError ? (
          <Grid item xs={12}>
            <Alert severity='error' onClose={() => loadStatistics()}>
              {statsError}
            </Alert>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color='textSecondary' gutterBottom>
                    Free Tier
                  </Typography>
                  <Typography variant='h4'>
                    {statistics?.officesByTier.free || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color='textSecondary' gutterBottom>
                    Small Tier
                  </Typography>
                  <Typography variant='h4'>
                    {statistics?.officesByTier.small || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color='textSecondary' gutterBottom>
                    Medium Tier
                  </Typography>
                  <Typography variant='h4'>
                    {statistics?.officesByTier.medium || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color='textSecondary' gutterBottom>
                    Large Tier
                  </Typography>
                  <Typography variant='h4'>
                    {statistics?.officesByTier.large || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Recent Administrative Actions */}
      <Typography variant='h5' gutterBottom sx={{ mt: 4 }}>
        Recent Administrative Actions
      </Typography>
      <Card>
        <CardContent>
          {isLoadingLogs ? (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              minHeight={200}
            >
              <CircularProgress size={40} />
            </Box>
          ) : logsError ? (
            <Alert severity='error' onClose={() => loadAuditLogs()}>
              {logsError}
            </Alert>
          ) : auditLogs.length === 0 ? (
            <Typography color='textSecondary' align='center' sx={{ py: 4 }}>
              No recent administrative actions
            </Typography>
          ) : (
            <List>
              {auditLogs.map((log, index) => (
                <React.Fragment key={log.id}>
                  <ListItem>
                    <ListItemText
                      primary={formatActionText(log)}
                      secondary={
                        <>
                          <Typography
                            component='span'
                            variant='body2'
                            color='textSecondary'
                          >
                            By: {log.adminEmail}
                          </Typography>
                          <br />
                          <Typography
                            component='span'
                            variant='body2'
                            color='textSecondary'
                          >
                            {formatTimestamp(log.timestamp)}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < auditLogs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

export default StatisticsDashboard
